import { useEffect, useMemo } from 'react';
import { throttle } from 'lodash-es';
import { withWindow } from '@shared/utils/withWindow';
import { useEventCallback } from './useEventCallback';

/**
 * Hook that listens to window resize events with automatic throttling.
 * Handles cross-browser resize event frequency differences by throttling at a consistent rate.
 *
 * @param callback - Function to call on resize (and immediately on mount)
 * @param delay - Throttle delay in milliseconds (default: 0ms)
 */
export const useThrottledWindowResize = (callback: () => void, delay: number = 0): void => {
  const stableCallback = useEventCallback(callback);

  const throttledCallback = useMemo(() => throttle(stableCallback, delay), [stableCallback, delay]);

  useEffect(() => {
    // Call immediately to get initial measurements
    stableCallback();

    return withWindow(
      global => {
        global.addEventListener('resize', throttledCallback);
        return () => {
          global.removeEventListener('resize', throttledCallback);
          throttledCallback.cancel();
        };
      },
      () => {},
    );
  }, [stableCallback, throttledCallback]);
};
